/**
 * Contrato da carteira em R$ exposto pelo BFF (CU-86akkme6x):
 * `GET /api/wallet/balance` e `GET /api/wallet/entries`.
 *
 * Valores são INTEIROS em micros (1 BRL = 1.000.000). O backend não devolve
 * string formatada de propósito — string de dinheiro no meio do caminho é como
 * se perde centavo. Formatar é do painel, via `MoneyMicrosPipe`.
 *
 * O BFF projeta campo por campo e NÃO devolve `pricing`, `metadata` nem
 * `idempotency_key`: margem e ids internos não vazam para o browser. Nada aqui
 * pode depender deles.
 */

import { WalletEntryObservation } from './WalletReports';

/** Micros por unidade monetária. Só existe para documentar a escala do contrato. */
export const MICROS_PER_UNIT = 1_000_000;

/** Um centavo em micros: valor em R$ digitado tem de ser múltiplo dele (2 casas). */
export const CENT_MICROS = 10_000;

/**
 * Valor em R$ com mais de 2 casas ("200.555"). É recusado com aviso, e não arredondado: o corpo
 * levaria um valor diferente do que a tela mostra (e `Math.round` em float ainda erra — 1.005 *
 * 100 = 100.4999…). Vale para a transferência do admin e para a recarga por valor do cliente.
 */
export const TOO_MANY_DECIMALS_PATTERN = /^\d*\.\d{3,}$/;

export interface WalletBalance {
  /** `null` é carteira que ainda não existe — resposta verdadeira, com zeros. */
  wallet_id: string | null;
  currency: string;
  balance_micros: number;
  held_micros: number;
  available_micros: number;
  overdraft: boolean;
}

/**
 * Saldo da carteira de UM usuário, dentro da resposta em lote de
 * `GET /api/admin/wallet/balances` (lista de Usuários do admin, task 17tm466nz8b —
 * pedido do Bruno em 17/10). `status`:
 *  - `ok`: `balance` tem o saldo de verdade.
 *  - `not_in_wallet`: o usuário não migrou nenhum canal para a carteira — `balance` é
 *    `null` de propósito (não é "zero", é "não migrou").
 *  - `unavailable`: a carteira não respondeu agora (circuito aberto, timeout, erro).
 *  - `disabled`: a carteira está desligada globalmente (nenhuma flag de cobrança ligada).
 *
 * `billing` é o mesmo formato de `WalletBilling` (mais abaixo neste arquivo) — repetido
 * em `Record` aqui porque `WalletBilling` só nasce mais adiante.
 */
export interface AdminWalletSummary {
  status: 'ok' | 'not_in_wallet' | 'unavailable' | 'disabled';
  balance: WalletBalance | null;
  billing: Record<'sms' | 'call' | 'whatsapp' | 'ai', 'wallet' | 'credits'> | null;
}

/**
 * Custo de UM canal cobrado pela carteira, no card "Cobrado pela carteira" da lista de
 * Usuários (subtask 17tm466vyfb, pedido do Misael em 30/10), dentro da resposta em lote de
 * `GET /api/admin/wallet/channel-costs`.
 *
 * - `unit_price_micros`: preço atual do cliente na `unit` (o mesmo preview da carteira que
 *   o débito usa). Na IA é o de ENTRADA e `unit_price_to_micros` o de SAÍDA; no RCS, Basic
 *   e Single. `null` = sem preço ou leitura que falhou, nunca zero.
 * - `spent_micros`: gasto do período (`period` do lote, mês corrente em Brasília), do
 *   resumo de consumo da carteira. `null` = resumo indisponível.
 */
export interface AdminWalletChannelCost {
  channel: string;
  unit: 'message' | 'minute' | '1k_tokens';
  unit_price_micros: number | null;
  unit_price_to_micros: number | null;
  price_source: WalletPriceSource;
  /** Só na IA: `provedor:modelo` cujo preço foi mostrado. */
  variant: string | null;
  spent_micros: number | null;
  quantity: number | null;
  /** A leitura do preço falhou agora (não é "sem preço": é "não consegui consultar"). */
  price_unavailable?: boolean;
}

/** Custos de um usuário. `status` como o de `AdminWalletSummary`; `partial` = parte das leituras falhou. */
export interface AdminWalletChannelCosts {
  status: 'ok' | 'not_in_wallet' | 'unavailable' | 'disabled';
  period: { from: string; to: string } | null;
  partial?: boolean;
  channels: AdminWalletChannelCost[];
}

export type WalletEntryType =
  | 'debit'
  | 'hold_capture'
  | 'credit'
  | 'bonus'
  | 'refund'
  | 'adjustment'
  | 'migration'
  | 'migration_reversal';

export interface WalletEntry {
  entry_id: string | null;
  entry_type: WalletEntryType | null;
  /** Vem COM SINAL da carteira: débito negativo, crédito positivo. */
  amount_micros: number;
  balance_after_micros: number;
  service: string | null;
  variant: string | null;
  quantity: number | null;
  hold_id: string | null;
  reference: string | null;
  /** ISO-8601 em UTC. */
  created_at: string | null;
  /**
   * Observação enriquecida (M7, task 17tm466nz8b). Só vem quando o pedido usa
   * `GET /wallet/entries?include=observation` (tela de Extrato) — o scan de saldo
   * não pede e nunca ganha este campo. `undefined` = não pedido; o contrato nunca
   * devolve `null` aqui (lançamento sem observação conhecida chega como `kind: 'unknown'`).
   */
  observation?: WalletEntryObservation;
}

export interface WalletEntryPage {
  data: WalletEntry[];
  /**
   * Cursor OPACO: codifica a posição no ledger e quem o interpreta é a carteira.
   * Repassar byte a byte; `null` significa fim do extrato.
   */
  next_cursor: string | null;
  /** Limite realmente aplicado — o teto de 200 pode ter cortado o pedido. */
  limit: number;
}

export interface WalletEntryFilters {
  limit?: number;
  cursor?: string | null;
  service?: string;
  entry_type?: WalletEntryType | '';
  /**
   * INSTANTE, não dia: o BFF converte o que chegar com `Carbon::parse`, então
   * data sem hora recorta em `00:00`. Quem monta o filtro manda as bordas do
   * dia (`day-bounds`); `null` é campo vazio, que o serviço não envia.
   */
  from?: string | null;
  to?: string | null;
  /**
   * `?include=observation` (M7, task 17tm466nz8b): pede a observação enriquecida de cada
   * lançamento. Só a tela de Extrato pede — o Dashboard soma o período pelo
   * `/wallet/usage-summary` e não lê lançamento, e pedir à toa custaria enriquecimento que
   * ninguém vai ler.
   */
  include?: 'observation';
}

/**
 * Rótulo do lançamento para o cliente. `hold_capture` é vocabulário de ledger e
 * não pode aparecer cru na tela. Até 07/10/2026 saía "Cobrança de reserva"; o Bruno
 * decidiu que, para quem lê o extrato, é débito como o da IA — o produto se separa
 * pela coluna/filtro de serviço, e o filtro "Débito" traz os dois (o BFF junta).
 */
export const WALLET_ENTRY_TYPE_LABELS: { [key: string]: string } = {
  debit: 'Débito',
  hold_capture: 'Débito',
  credit: 'Crédito',
  bonus: 'Bônus',
  refund: 'Estorno',
  adjustment: 'Ajuste',
  migration: 'Migração de saldo',
  migration_reversal: 'Reversão de migração',
};

/** Fallback quando a carteira mandar um tipo que este painel ainda não conhece. */
export const WALLET_ENTRY_TYPE_FALLBACK_LABEL = 'Lançamento';

/**
 * Tipos aceitos pelo BFF no filtro `entry_type`.
 *
 * `hold_capture` aparece no extrato mas ficou FORA da allowlist do
 * `WalletEntriesRequest`: oferecê-lo no filtro renderia 422 na cara do cliente.
 *
 * `bonus` entra (CU-86akn0zp6): é lançamento próprio da recarga por valor, e sem
 * ele no filtro o cliente não consegue isolar o que ganhou do que pagou.
 */
export const WALLET_ENTRY_TYPE_FILTERS: WalletEntryType[] = [
  'debit',
  'credit',
  'bonus',
  'refund',
  'adjustment',
  'migration',
  'migration_reversal',
];

/**
 * Serviço cobrado. É allowlist de rótulo, não de valores: `sms` e `call` entram
 * na carteira em fases seguintes, então o que não estiver aqui sai como veio em
 * vez de virar rótulo vazio.
 */
export const WALLET_SERVICE_LABELS: { [key: string]: string } = {
  whatsapp: 'WhatsApp',
  ai: 'IA',
  sms: 'SMS',
  call: 'Ligação',
  recharge: 'Recarga',
  // RCS (17tm466frtx): a carteira cobra em dois serviços, pelo formato efetivo da mensagem.
  rcs_basic: 'RCS Basic',
  rcs_single: 'RCS Single',
};

/**
 * A unidade que o microserviço cobra em cada serviço
 * (`WalletCatalogService::UNITS_BY_SERVICE`). Está aqui porque `quantity` no
 * extrato NÃO é "envios" em todo canal: em SMS e WhatsApp é mensagem, em
 * ligação é bloco de 30 segundos e em IA é bloco de mil tokens. Somar os quatro
 * sob o rótulo "envios" misturaria semânticas diferentes, então cada linha diz
 * a sua unidade.
 */
export const WALLET_SERVICE_UNIT_LABELS: { [key: string]: string } = {
  sms: 'mensagens',
  whatsapp: 'mensagens',
  call: 'blocos de 30s',
  ai: 'blocos de 1k tokens',
  rcs_basic: 'mensagens',
  rcs_single: 'mensagens',
};

export const WALLET_SERVICE_UNIT_FALLBACK_LABEL = 'unidades';

/** Consumo de um serviço num período (uma linha do "Gasto por canal" do Dashboard). */
export interface WalletServiceUsage {
  service: string;
  label: string;
  unitLabel: string;
  /**
   * `null` quando algum débito do serviço veio sem `quantity` — a carteira
   * cobra IA por tokens e às vezes não devolve contagem. Zero seria mentira:
   * houve consumo, o que não houve foi contagem.
   */
  quantity: number | null;
  /** Positivo: quanto SAIU da carteira. Estorno abate. */
  spentMicros: number;
  entriesCount: number;
}

/** Canais que o resumo agregado no servidor SEMPRE traz (17tm466t0ca). */
export type WalletUsageService = 'sms' | 'call' | 'whatsapp' | 'ai';

/**
 * Os quatro canais de sempre, na ordem em que o "Gasto por canal" os lista no
 * empate. A carteira devolve TODO serviço que cobra (o RCS entra como
 * `rcs_basic`/`rcs_single`), então o resumo pode trazer chaves além destas.
 */
export const WALLET_USAGE_KNOWN_SERVICES: WalletUsageService[] = ['sms', 'call', 'whatsapp', 'ai'];

/** Um bucket (dia ou total) do resumo de consumo — já líquido de estornos. */
export interface WalletUsageBucket {
  entries: number;
  /** `null` só para `ai`: a carteira não conta tokens como unidade do extrato. */
  quantity: number | null;
  spent_micros: number;
}

/**
 * Buckets por serviço do resumo: os quatro de `WalletUsageService` sempre
 * presentes, mais qualquer serviço que a carteira passar a cobrar (`rcs_basic`...).
 */
export type WalletUsageBuckets = Record<WalletUsageService, WalletUsageBucket> & { [service: string]: WalletUsageBucket };

/**
 * Resposta de `GET /wallet/usage-summary` (17tm466t0ca): consumo do período
 * somado na carteira, por dia (data civil em `timezone`, America/Sao_Paulo) e
 * por canal — sem paginação e sem teto de lançamentos. Alimenta os três blocos
 * do Dashboard que dependiam do extrato (gráfico, total consumido, gasto por canal).
 */
export interface WalletUsageSummary {
  currency: string;
  timezone: string;
  from: string;
  to: string;
  days: Array<{ date: string; services: WalletUsageBuckets }>;
  totals: WalletUsageBuckets;
  /** Soma de TODOS os serviços cobrados no período, inclusive os que o painel não conhece pelo nome. */
  total_spent_micros: number;
}

/**
 * Serviços cuja `quantity` do resumo NÃO fecha como total (betaHml, RCS 17tm466frtx): o acerto
 * do envio em massa RCS entra na carteira como valor cheio, SEM `quantity`, enquanto as etapas
 * avulsas entram com 1 cada; o resumo soma só as quantidades não nulas (o `SUM` do banco pula
 * NULL), então a contagem sairia parcial. Soma parcial com cara de total é pior que "sem
 * contagem" — a mesma regra que o `aggregateUsageByService` aplicava no extrato do HML. O
 * GASTO (`spent_micros`) desses serviços continua inteiro; só a contagem some. A contagem de
 * RCS do período está no bloco RCS (`/daily-sents`). Sai daqui quando o resumo devolver
 * `quantity: null` para serviço com débito sem contagem.
 */
export const WALLET_USAGE_UNCOUNTED_SERVICES: ReadonlyArray<string> = ['rcs_basic', 'rcs_single'];

/**
 * "Gasto por canal" a partir do resumo do servidor. Lê TODAS as chaves de
 * `totals`, não uma lista fixa: serviço novo que a carteira passar a cobrar
 * (ex. `rcs_basic`) aparece sem mudar o painel, com o rótulo de
 * `walletServiceLabel` (a própria chave quando não há rótulo) e a unidade
 * genérica. Só entram canais com lançamento no período.
 *
 * Ordem: maior gasto primeiro; no empate, os quatro conhecidos na ordem de
 * `WALLET_USAGE_KNOWN_SERVICES` e depois os demais em ordem alfabética (o
 * `sort` é estável), para a lista não trocar de lugar entre uma carga e outra.
 */
export function usageFromSummary(summary: WalletUsageSummary | null | undefined): WalletServiceUsage[] {
  const totals: { [service: string]: WalletUsageBucket } = summary?.totals ?? {};

  return orderedUsageServices(Object.keys(totals))
    .map((service) => ({ service, bucket: totals[service] }))
    .filter(({ bucket }) => !!bucket && bucket.entries > 0)
    .map(({ service, bucket }) => ({
      service,
      label: walletServiceLabel(service),
      unitLabel: WALLET_SERVICE_UNIT_LABELS[service] || WALLET_SERVICE_UNIT_FALLBACK_LABEL,
      quantity: WALLET_USAGE_UNCOUNTED_SERVICES.includes(service) ? null : bucket.quantity ?? null,
      spentMicros: bucket.spent_micros || 0,
      entriesCount: bucket.entries,
    }))
    .sort((a, b) => b.spentMicros - a.spentMicros);
}

/**
 * Total consumido do período em "Tudo": o `total_spent_micros` do servidor, que
 * já soma todos os serviços cobrados. Resposta sem ele cai na soma dos `totals`.
 */
export function usageSummaryTotalMicros(summary: WalletUsageSummary | null | undefined): number {
  if (!summary) {
    return 0;
  }

  if (typeof summary.total_spent_micros === 'number') {
    return summary.total_spent_micros;
  }

  return Object.values(summary.totals ?? {}).reduce((total, bucket) => total + (bucket?.spent_micros || 0), 0);
}

/** Os conhecidos na ordem de sempre, depois os demais em ordem alfabética. */
function orderedUsageServices(services: string[]): string[] {
  const known: string[] = WALLET_USAGE_KNOWN_SERVICES.filter((service) => services.includes(service));
  const others = services.filter((service) => !known.includes(service)).sort();

  return [...known, ...others];
}

export function walletEntryTypeLabel(entryType: string | null): string {
  if (!entryType) {
    return WALLET_ENTRY_TYPE_FALLBACK_LABEL;
  }

  return WALLET_ENTRY_TYPE_LABELS[entryType] || WALLET_ENTRY_TYPE_FALLBACK_LABEL;
}

export function walletServiceLabel(service: string | null): string {
  if (!service) {
    return '—';
  }

  return WALLET_SERVICE_LABELS[service] || service;
}

/**
 * Os três desfechos de erro do contrato, cada um uma UI diferente.
 *
 * - `disabled`  403 `wallet_disabled` — nenhuma flag de cobrança ligada. Não é
 *   erro: é conta sem carteira habilitada.
 * - `unavailable` 503 `wallet_unavailable` — carteira fora do ar ou circuito
 *   aberto. Estado previsto, com retentativa prevista. NUNCA vira saldo zero:
 *   zero é resposta verdadeira do contrato e confundir os dois faz o cliente
 *   achar que o dinheiro sumiu.
 * - `invalid_filter` 422 — filtro inválido que o próprio painel mandou.
 */
export type WalletErrorKind = 'disabled' | 'unavailable' | 'invalid_filter' | 'unknown';

export interface WalletError {
  kind: WalletErrorKind;
  message: string;
  /** Do header `Retry-After`; `null` quando o header não chega. */
  retryAfterSeconds: number | null;
}

/**
 * Preço por produto exposto por `GET /wallet/prices` (CU-86akn0zp6).
 *
 * A carteira é uma só, mas o preço do envio é por conta × produto e sai de uma
 * cascata: contrato → preço de assinante → preço padrão. `source` diz qual das
 * três valeu, e é isso que a tela mostra ao lado do valor — sem ele o cliente vê
 * um número e não sabe por que ele é aquele.
 */
export type WalletProductService = 'sms' | 'whatsapp' | 'call';

/** `quantity` do extrato conta nesta unidade. Ver `WALLET_SERVICE_UNIT_LABELS`. */
export type WalletProductUnit = 'message' | 'block_30s';

/**
 * De onde o preço saiu. `cost` é preço de custo (conta interna) e `null` é
 * produto sem preço resolvido — que vem junto de `blocked: true`.
 */
export type WalletPriceSource = 'contract' | 'subscriber' | 'standard' | 'cost' | null;

/** Faixa de preço vigente para a conta inteira, não por produto. */
export type WalletPricingTier = 'standard' | 'subscriber';

export interface WalletProductPrice {
  service: WalletProductService;
  unit: WalletProductUnit;
  /** `null` quando o produto está bloqueado — não é zero, é ausência de preço. */
  unit_price_micros: number | null;
  /**
   * O que o envio custaria no preço padrão. Só interessa a quem está em
   * `subscriber`: é o número do aviso de queda de assinatura.
   */
  standard_unit_price_micros?: number | null;
  source: WalletPriceSource;
  blocked: boolean;
}

export interface WalletSubscription {
  plan_id: string;
  plan_name: string;
  /** ISO-8601. */
  expiration_date: string;
}

export interface WalletPrices {
  pricing_tier: WalletPricingTier;
  /** `null` é conta sem assinatura — é o gatilho do banner de planos no painel. */
  subscription: WalletSubscription | null;
  products: WalletProductPrice[];
}

/** Rótulo da origem do preço, para o badge ao lado do valor. */
export const WALLET_PRICE_SOURCE_LABELS: { [key: string]: string } = {
  contract: 'Preço de contrato',
  subscriber: 'Preço de assinante',
  standard: 'Preço padrão',
  cost: 'Preço de custo',
};

/** Produto sem preço resolvido. Não é `R$ 0,00`: é envio que não sai. */
export const WALLET_PRICE_BLOCKED_LABEL = 'sem preço definido';

/**
 * Sufixo do preço unitário. A unidade muda de produto para produto e sem ela
 * "R$ 0,10" em ligação passaria por preço da chamada inteira.
 */
export const WALLET_PRODUCT_UNIT_SUFFIXES: { [key: string]: string } = {
  sms: '/ SMS enviado',
  whatsapp: '/ mensagem de WhatsApp',
  call: '/ bloco de 30 s de ligação',
};

export const WALLET_PRODUCT_UNIT_FALLBACK_SUFFIX = '/ unidade';

export function walletPriceSourceLabel(source: WalletPriceSource): string {
  if (!source) {
    return WALLET_PRICE_BLOCKED_LABEL;
  }

  return WALLET_PRICE_SOURCE_LABELS[source] || WALLET_PRICE_BLOCKED_LABEL;
}

export function walletProductUnitSuffix(service: string | null): string {
  if (!service) {
    return WALLET_PRODUCT_UNIT_FALLBACK_SUFFIX;
  }

  return WALLET_PRODUCT_UNIT_SUFFIXES[service] || WALLET_PRODUCT_UNIT_FALLBACK_SUFFIX;
}

/** Acha o produto do canal escolhido; `null` quando a carteira não o devolveu. */
export function findProductPrice(
  prices: WalletPrices | null,
  service: string | null
): WalletProductPrice | null {
  if (!prices || !prices.products || !service) {
    return null;
  }

  return prices.products.find((product) => product.service === service) || null;
}

/**
 * Faixa de bônus da recarga por valor: "a partir de R$ 200 → +R$ 10".
 *
 * As faixas vêm da API, nunca do painel: bônus é dinheiro creditado e a régua
 * tem de ser a mesma que o backend vai aplicar no `POST /recharges`.
 */
export interface BonusTier {
  min_total_micros: number;
  bonus_micros: number;
}

/**
 * Bônus zero porque a conta está fora da coorte da carteira: a recarga é por
 * quantidade e não recebe bônus de valor. Devolvido pela cotação
 * (`GET /recharges/bonus-quote`) e pela recarga manual do admin.
 *
 * NOME PROVISÓRIO do backend (25/09/2026): mora só aqui; o painel do admin
 * (`WalletBonus.ts`) importa daqui.
 */
export const BONUS_REASON_NOT_IN_WALLET_COHORT = 'not_in_wallet_cohort' as const;

/**
 * Por que o bônus saiu (ou não). `ok` e `no_tier` são desfechos normais — o
 * valor simplesmente não alcançou faixa nenhuma; os outros três são exclusões
 * que o cliente precisa ler em texto, senão o resumo mostra `+ R$ 0,00` sem
 * explicar por quê.
 */
export type BonusQuoteReason =
  | 'ok'
  | 'no_tier'
  | 'subscriber_excluded'
  | 'account_price_excluded'
  | 'wallet_unavailable'
  | typeof BONUS_REASON_ACCOUNT_OPT_OUT
  | typeof BONUS_REASON_NOT_IN_WALLET_COHORT;

/**
 * A conta tem "Recebe bônus de recarga" desligada (CU-17tm466tzkz). Sem rótulo de
 * propósito: para essa conta a tela não fala de bônus nenhum — nem faixa, nem linha
 * de bônus, nem aviso.
 */
export const BONUS_REASON_ACCOUNT_OPT_OUT = 'account_opt_out' as const;

export interface BonusQuote {
  paid_micros: number;
  bonus_micros: number;
  /** Faixa que pegou; `null` quando nenhuma pegou. */
  tier: BonusTier | null;
  reason: BonusQuoteReason;
  /** Todas as faixas, para os chips do modal. Vazia quando a conta não recebe bônus. */
  tiers: BonusTier[];
  /**
   * `false` quando a conta não recebe bônus de recarga (CU-17tm466tzkz). Ausente em
   * backend anterior à config = recebe.
   */
  bonus_eligible?: boolean;
  /** Recarga mínima aceita pelo backend. */
  min_total_micros: number;
  pricing_tier: WalletPricingTier;
}

/**
 * Motivo em texto de gente. `ok` e `no_tier` ficam de fora de propósito: não há
 * o que explicar quando o bônus saiu, nem quando o valor só não alcançou faixa
 * — o resumo já mostra o bônus zerado e os chips mostram a partir de quanto.
 */
export const BONUS_QUOTE_REASON_LABELS: { [key: string]: string } = {
  subscriber_excluded: 'Assinantes não acumulam bônus.',
  account_price_excluded: 'Sua conta tem preço de contrato, e o preço de contrato não acumula com o bônus de recarga. O valor pago entra inteiro na carteira.',
  wallet_unavailable: 'Não foi possível calcular o bônus agora — o valor pago é creditado do mesmo jeito.',
  [BONUS_REASON_NOT_IN_WALLET_COHORT]: 'Sua conta ainda não usa a carteira em R$, então esta recarga não recebe bônus.',
};

export function bonusQuoteReasonLabel(reason: BonusQuoteReason | null): string | null {
  if (!reason) {
    return null;
  }

  return BONUS_QUOTE_REASON_LABELS[reason] || null;
}

/**
 * Micros para o decimal que o contrato pede em `total` ("200.00").
 *
 * String, e com ponto: o backend valida `total` como decimal e mandar number
 * deixaria o JSON escolher a notação (`2e+2`), que não passa na validação.
 */
export function microsToDecimalString(micros: number): string {
  return (Math.round(micros) / MICROS_PER_UNIT).toFixed(2);
}

/** Reais para micros inteiros. É o caminho de volta do campo de valor. */
export function unitsToMicros(units: number): number {
  return Math.round(units * MICROS_PER_UNIT);
}

/**
 * De onde sai a cobrança de cada canal da conta (`GET /wallet/billing`, 28/09/2026).
 * `credits` é o pacote de sempre (quantidade); `wallet` é o saldo em R$.
 */
export type WalletBillingSource = 'wallet' | 'credits';

export interface WalletBilling {
  sms: WalletBillingSource;
  call: WalletBillingSource;
  whatsapp: WalletBillingSource;
  ai: WalletBillingSource;
}

/**
 * Reserva (hold) da carteira, como o BFF devolve (`GET /wallet/holds`, 07/10/2026).
 * Reserva não é lançamento: prende o disponível sem mexer no saldo. O extrato mostra
 * as ativas ("Reservado") e o que voltou delas ("Reserva devolvida").
 */
export type WalletHoldStatus = 'active' | 'captured' | 'released' | 'expired';

export interface WalletHold {
  hold_id: string;
  status: WalletHoldStatus;
  amount_micros: number;
  /** 0 enquanto não houve captura. */
  captured_micros: number;
  service: string | null;
  quantity: number | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface WalletHoldFilters {
  status?: string;
  service?: string;
  from?: string | null;
  to?: string | null;
  limit?: number;
}

/** Linha de reserva no extrato: o que está preso agora ou o que voltou ao disponível. */
export interface WalletHoldRow {
  kind: 'hold';
  state: 'reserved' | 'returned';
  hold: WalletHold;
  /** Valor da linha: o reservado (ativa) ou o que voltou (devolvida, ou a sobra de uma captura parcial). */
  amountMicros: number;
  /** Instante da linha: criação da reserva ativa; desfecho da devolvida. */
  at: string;
}

/**
 * Transforma reservas em linhas do extrato. Captura total não vira linha: o Débito
 * já está no extrato. Captura parcial vira "devolvida" pela sobra.
 */
export function walletHoldRows(holds: WalletHold[]): WalletHoldRow[] {
  const rows: WalletHoldRow[] = [];

  (holds || []).forEach((hold) => {
    if (!hold) { return; }

    if (hold.status === 'active') {
      rows.push({ kind: 'hold', state: 'reserved', hold, amountMicros: hold.amount_micros, at: hold.created_at });
      return;
    }

    const returned = hold.status === 'captured'
      ? hold.amount_micros - (hold.captured_micros || 0)
      : hold.amount_micros;

    if (returned > 0) {
      rows.push({ kind: 'hold', state: 'returned', hold, amountMicros: returned, at: hold.updated_at });
    }
  });

  return rows;
}
